import { useState, useCallback } from 'react'

/**
 * useState backed by sessionStorage.
 * State survives tab switches and F5 reloads but resets when the browser/tab is closed.
 * Falls back gracefully in Safari private mode (sessionStorage throws).
 *
 * @param {string} key - sessionStorage key
 * @param {*} defaultValue - initial value if nothing is stored
 * @returns {[*, Function]} [value, setter]
 */
export function useSessionState(key, defaultValue) {
    const [value, setValueRaw] = useState(() => {
        try {
            const stored = sessionStorage.getItem(key)
            if (stored === null) return defaultValue
            return JSON.parse(stored)
        } catch {
            return defaultValue
        }
    })

    const setValue = useCallback((next) => {
        setValueRaw(prev => {
            const resolved = typeof next === 'function' ? next(prev) : next
            try {
                if (resolved === null || resolved === undefined) {
                    sessionStorage.removeItem(key)
                } else {
                    sessionStorage.setItem(key, JSON.stringify(resolved))
                }
            } catch {}
            return resolved
        })
    }, [key])

    return [value, setValue]
}
